import { CapSeveritySchema } from '@xweather/protect-types'
import type { Geometry } from 'geojson'
import { z } from 'zod'

export const ProtectionType = {
	Hail: 'hail',
	Lightning: 'lightning',
	Measurement: 'measurement',
	Precipitation: 'precipitation',
	WeatherHazard: 'weatherHazard',
	WebRelay: 'webRelay',
	Wind: 'wind',
} as const
export type ProtectionType = (typeof ProtectionType)[keyof typeof ProtectionType]

export type AlertEngineCompatibleProtectionType = Exclude<ProtectionType, 'measurement' | 'webRelay'>

export const ParameterType = {
	Realtime: 'realtime',
	Forecast: 'forecast',
} as const
export type ParameterType = (typeof ParameterType)[keyof typeof ParameterType]

export const ProtectionPriority = {
	LightningAlarm: 1,
	LightningWarning: 2,
	LightningInfo: 3,
	LightningInfo2: 4,
	Hail: 4,
	Wind: 5,
	WeatherHazard: 6,
	// TODO: Placeholder — position in the priority space is an open product question, not yet decided.
	Precipitation: 7,
} as const
export type ProtectionPriority = (typeof ProtectionPriority)[keyof typeof ProtectionPriority]

export const IncomingLightningPriority = {
	Alarm: ProtectionPriority.LightningAlarm,
	Warning: ProtectionPriority.LightningWarning,
	Info: ProtectionPriority.LightningInfo,
	Info2: ProtectionPriority.LightningInfo2,
} as const

const ProtectionTypeSchema = z.enum(['hail', 'lightning', 'wind', 'measurement', 'weatherHazard', 'webRelay', 'precipitation'])
const ProtectionSchema = z.object({
	type: ProtectionTypeSchema,
	parameterType: z.enum(['realtime', 'forecast']),
	priority: z.number(),
	vrn: z.string(),
	assetVrn: z.string(),
	createdTime: z.string(),
})

// TODO: Replace with type derived from ProtectionSchema as well as any underlying types i.e. ProtectionType
export interface Protection {
	type: ProtectionType
	parameterType: ParameterType
	priority: ProtectionPriority
	vrn: string
	assetVrn: string
	createdTime: string
}

export interface ImmediateUserList {
	vrn: string
}

export interface ScheduledUserList {
	vrn: string
	times: string[]
	timezone: string
}

export interface UserLists {
	immediate?: ImmediateUserList[]
	scheduled?: ScheduledUserList[]
}

export type RequiredUserLists<K extends keyof UserLists> = Required<Pick<UserLists, K>>

export interface ProtectionConfiguration {
	guidelines?: string
	userLists: UserLists
}

export interface HailProtection extends Protection {
	type: typeof ProtectionType.Hail
	priority: typeof ProtectionPriority.Hail
}

export interface ForecastHailProtection extends HailProtection, ProtectionConfiguration {
	parameterType: typeof ParameterType.Forecast
	horizonMinutes: number
	HAIL_SEVERE_PROBABILITY: number
	bufferKm: number
	activeTimeoutMinutes: number
	userLists: RequiredUserLists<'immediate'>
}

export interface LightningProtection extends Protection {
	type: typeof ProtectionType.Lightning
	priority:
		| typeof ProtectionPriority.LightningAlarm
		| typeof ProtectionPriority.LightningWarning
		| typeof ProtectionPriority.LightningInfo
		| typeof ProtectionPriority.LightningInfo2
	geometry: Geometry
	radius: number
	areaId: string
}

export interface RealtimeLightningProtection extends LightningProtection, ProtectionConfiguration {
	parameterType: typeof ParameterType.Realtime
	radius: number
	clearTime: number
	includeCloudToCloud: boolean
	enabled: boolean
	userLists: RequiredUserLists<'immediate'>
}

export interface ForecastLightningProtection extends LightningProtection, ProtectionConfiguration {
	parameterType: typeof ParameterType.Forecast
	horizonMinutes: number
	enabled: boolean
	userLists: RequiredUserLists<'immediate'>
}

export interface LightningProtectionGroup {
	type: typeof ProtectionType.Lightning
	assetVrn: string
	rings: {
		alarm?: RealtimeLightningProtection
		warning?: RealtimeLightningProtection
		info?: RealtimeLightningProtection
		info2?: RealtimeLightningProtection
	}
	ltz?: ForecastLightningProtection
}

export interface WindProtection extends Protection {
	type: typeof ProtectionType.Wind
	priority: typeof ProtectionPriority.Wind
}

export interface ForecastWindProtection extends WindProtection, ProtectionConfiguration {
	parameterType: typeof ParameterType.Forecast
	WIND_SPEED?: number
	WIND_GUST?: number
	height: number
	horizonMinutes: number
	operation: 'AND' | 'OR'
	userLists: RequiredUserLists<'scheduled'>
}

const ImmediateUserListsSchema = z.object({
	immediate: z.array(z.object({ vrn: z.string() })),
})

const WeatherHazardProtectionSchema = ProtectionSchema.extend({
	type: z.literal('weatherHazard'),
	priority: z.literal(ProtectionPriority.WeatherHazard),
})

export const FullWeatherHazardProtectionSchema = WeatherHazardProtectionSchema.extend({
	capSeverity: z.array(CapSeveritySchema),
	userLists: ImmediateUserListsSchema,
})

export type WeatherHazardProtection = z.infer<typeof WeatherHazardProtectionSchema>
export type FullWeatherHazardProtection = z.infer<typeof FullWeatherHazardProtectionSchema>

export interface PrecipitationProtection extends Protection {
	type: typeof ProtectionType.Precipitation
	priority: typeof ProtectionPriority.Precipitation
}

export interface ForecastPrecipitationProtection extends PrecipitationProtection, ProtectionConfiguration {
	parameterType: typeof ParameterType.Forecast
	horizonMinutes: number
	PRECIPITATION_LIQUID_ACCUM_1HR_MM?: number
	PRECIPITATION_LIQUID_ACCUM_6HR_MM?: number
	PRECIPITATION_LIQUID_ACCUM_24HR_MM?: number
	PRECIPITATION_LIQUID_ACCUM_72HR_MM?: number
	userLists: RequiredUserLists<'scheduled'>
}

export type AnyProtection = HailProtection | LightningProtection | WindProtection | WeatherHazardProtection | PrecipitationProtection
export type AnyFullProtection =
	| ForecastHailProtection
	| ForecastWindProtection
	| FullWeatherHazardProtection
	| ForecastPrecipitationProtection
